import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import {compose} from "mu-lambda";
import * as React from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {HeadingLarge} from "@khanacademy/wonder-blocks-typography";
import {
    withMarkConversion,
    type WithMarkConversionProps,
    type WithoutMarkConversion,
} from "@repo/analytics-learning-time";
import Log, {Errors} from "@repo/logging";

import RouteLoadSpinner from "#app-shell-package/route-load-spinner.tsx";
import withUserProfile, {
    type WithoutUserProfile,
    type WithUserProfileProps,
} from "#data-access/with-user-profile.tsx";
import type Backbone from "#shared-package/backbone-lite.js";

import Error from "./error.tsx";
import {profileLessFiles, type Profile} from "./profile-util.ts";

type ProfileWidgetsView =
    typeof import("../../profile-package/widgets/profile-widgets.tsx").ProfileWidgetsView;

type Props = {
    kaid: string;
    profilePromise: Promise<Profile>;
    isMyPage: boolean;
    onSufficientlyUsable: () => void;
} & WithMarkConversionProps &
    WithUserProfileProps;

type State = {
    loading: boolean;
    profile?: Profile;
    userProfileWidgets?: Backbone.Model;
    ProfileWidgetsView?: ProfileWidgetsView;
    showError: boolean;
};

class ProfileView extends React.Component<Props, State> {
    // @ts-expect-error - TS2564 - Property '_isMounted' has no initializer and is not definitely assigned in the constructor.
    _isMounted: boolean;

    state: State = {
        loading: true,
        userProfileWidgets: undefined,
        ProfileWidgetsView: undefined,
        showError: false,
    };

    componentDidMount() {
        this.updateWidgets();
    }

    componentDidUpdate(prevProps: Props) {
        if (
            this.props.kaid !== prevProps.kaid ||
            (prevProps.profileLoading && !this.props.profileLoading)
        ) {
            this.updateWidgets();
        }
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    updateWidgets() {
        const {
            profilePromise,
            markConversion,
            onSufficientlyUsable,
            profileLoading,
            profileError,
        } = this.props;
        markConversion({id: "logged_in_homepage_visit"});
        const promises: Array<Promise<any>> = [
            profilePromise,
            import("../../profile-package/widgets/profile-widgets.tsx"),
            import("../../profile-package/profile-tabs.tsx").then(
                (m) => m.default,
            ),
            ...profileLessFiles(),
        ];

        this.setState({
            userProfileWidgets: undefined,
            ProfileWidgetsView: undefined,
            loading: true,
        });

        Promise.all(promises)
            .then(
                // @ts-expect-error - TS2345 - Argument of type '([profile, { ProfileWidgetCollection, ProfileWidgetsView }, ProfileTabs]: [any, any, any]) => any' is not assignable to parameter of type '(value: any[]) => any'.
                ([
                    profile,
                    {ProfileWidgetCollection, ProfileWidgetsView},
                    ProfileTabs,
                ]: [any, any, any]) => {
                    if (!this._isMounted || !profile.profile) {
                        return;
                    }

                    if (profileLoading || profileError) {
                        return;
                    }

                    // Purposely using `this.props.profile` in order to avoid
                    // ambiguous naming issues (this is the actor's info).
                    const actorKaid = this.props.profile?.get("kaid") || "";
                    const userProfileWidgets = new ProfileWidgetCollection({
                        profileKaid: profile.profile.get("kaid"),
                        actorKaid,
                    });

                    // TODO(briang): Use a similar caching mechanism for this like
                    // the profile for a perf win!
                    return userProfileWidgets.fetch({
                        success: () => {
                            if (!this._isMounted) {
                                return;
                            }

                            // Force a reload of the display case
                            ProfileTabs.resetDisplayCase();

                            this.setState({
                                userProfileWidgets,
                                ProfileWidgetsView,
                                loading: false,
                            });

                            onSufficientlyUsable();
                        },
                    });
                },
            )
            .catch((error) => {
                if (this._isMounted) {
                    Log.error(
                        "Error initializing Widgets profile:",
                        Errors.Internal,
                        {cause: error},
                    );
                    this.setState({showError: true});
                }
            });

        this._isMounted = true;
    }

    render(): React.ReactNode {
        const {isMyPage} = this.props;
        const {loading, userProfileWidgets, ProfileWidgetsView, showError} =
            this.state;

        if (showError) {
            return <Error />;
        }

        return (
            <View style={styles.container} testId="my-profile">
                <HeadingLarge>
                    {isMyPage ? t`My profile` : t`Profile`}
                </HeadingLarge>
                {loading && <RouteLoadSpinner />}
                {!loading && (
                    <div
                        id="tab-content-coaches"
                        className="lihp-profile-widgets"
                    >
                        {ProfileWidgetsView && (
                            <ProfileWidgetsView
                                collection={userProfileWidgets}
                                newLIHP={true}
                                breakpoint="lg"
                            />
                        )}
                    </div>
                )}
            </View>
        );
    }
}

const styles = StyleSheet.create({
    container: {
        marginBottom: 16,
    },
});

type ExportProps = WithoutUserProfile<
    WithoutMarkConversion<
        JSX.LibraryManagedAttributes<
            typeof ProfileView,
            React.ComponentProps<typeof ProfileView>
        >
    >
>;

export default compose(
    withUserProfile,
    withMarkConversion,
)(ProfileView) as React.ComponentType<ExportProps>;
